import classNames from 'classnames';
import { lazy, Suspense, ComponentProps } from 'react';

import { Skeleton } from '@zooplus/ui-components';

import styles from './ProgramCardIcon.module.css';

const BasicCardIcon = lazy(() => import('./basic-card.svg'));
const PremiumCardIcon = lazy(() => import('./premium-card.svg'));

export interface ProgramCardIconProps extends ComponentProps<typeof BasicCardIcon> {
  level?: 'basic' | 'premium';
  size?: 'normal' | 'small';
  variant?: 'default' | 'rotated';
}

const cardIcons = {
  basic: BasicCardIcon,
  premium: PremiumCardIcon,
};

/**
 * The `ProgramCardIcon` component displays the Loyalty Program card icon based on the program level.
 *
 * Provide the `level` prop to display basic or premium card.
 *
 * With the `size` and `variant` prop you can customize the card's appearance in order
 * to display rotated and/or small icon.
 *
 * The component accepts all valid SVG props, which are passed down to the underlying SVG element.
 * The element is hidden to assistive technologies by default, but you can override this behavior by passing
 * the relevant props (like `aria-hidden`, `aria-label`, etc).
 *
 * As the SVGs are lazy loaded, a skeleton placeholder is automatically shown.
 */
export const ProgramCardIcon = ({
  level = 'basic',
  size = 'normal',
  variant = 'default',
  className,
  ...svgProps
}: ProgramCardIconProps) => {
  const cardClass = classNames(className, {
    [styles.rotated]: variant === 'rotated',
    [styles.small]: size === 'small',
  });

  const Card = cardIcons[level];

  return (
    <Suspense fallback={<Skeleton width="84px" height="60px" />}>
      <Card className={cardClass} aria-hidden {...svgProps} />
    </Suspense>
  );
};
